import { Box, CircularProgress } from '@mui/material';
import React, { ComponentType, lazy } from 'react';

type ComponentImport<T extends ComponentType<unknown>> = () => Promise<{ default: T }>;

/**
 * Force the refresh of the page ONCE when you encounter this error
 * @link https://raphael-leger.medium.com/react-webpack-chunkloaderror-loading-chunk-x-failed-ac385bd110e0
 *
 * Exported for a component that supplies its own Suspense boundary, where the full-screen
 * fallback below would cover the page.
 */
export const lazyWithRetry = <T extends ComponentType<unknown>>(componentImport: ComponentImport<T>) =>
    lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();

            window.localStorage.setItem('page-has-been-force-refreshed', 'false');

            return component;
        } catch (error) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // Assuming that the user is not on the latest version of the application.
                // Let's refresh the page immediately.
                window.localStorage.setItem('page-has-been-force-refreshed', 'true');
                window.location.reload();

                return new Promise<never>(() => {});
            }

            // The page has already been reloaded
            // Assuming that user is already using the latest version of the application.
            // Let's let the application crash and raise the error.
            throw error;
        }
    });

export default function lazyWithSuspenseAndRetry<T extends ComponentType<any>>(
    componentImport: ComponentImport<T>,
    removeLoading = false
) {
    const Component = lazyWithRetry(componentImport);

    return function LazyComponent({ ...props }: React.ComponentProps<T>) {
        return (
            <React.Suspense
                fallback={
                    <Box
                        aria-label="Loading..."
                        sx={{
                            display: 'flex',
                            flex: 1,
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'fixed',
                        }}>
                        {!removeLoading && <CircularProgress sx={{ alignSelf: 'center' }} />}
                    </Box>
                }>
                <Component {...props} />
            </React.Suspense>
        );
    };
}
